import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { ServiceStatus } from "../../../types/serviceTypes";

import { INCOMMODITY_SATISFACTION_MANAGER } from "../../../consts/incommodity";

import {
  GoodsServiceRequestDetail,
  cancelGoodsServiceRequest,
  getGoodsServiceDetail,
  getServiceRequestStep,
} from "../../../services/goodsRequestServices";

import { getYearToMinuteFromISODateString } from "../../../libs/dateFormat";

import { useUserContext } from "../../../contexts/userContext";

import {
  patchEtcSatisfaction,
  postEtcSatisfaction,
} from "../../../services/etcServices";
import { SatisfactionRequest } from "../../../services/userReportServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonButton from "../../../components/Button";
import StickyHeader from "../../../components/Layout/StickyHeader";
import ProgressBar from "../../../components/ProgressBar";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonPopup from "../../../components/Popup";
import HistorySatisfaction, {
  SatisfactionReplyItem,
} from "../../../components/History/Satisfaction";
import BottomGroup from "../../../components/Button/Group";
import UserSatisfactionPopup from "../../../components/Popup/UserSatisfaction";
import HistoryDetailRow from "../../../components/History/DetailRow";
import HistoryDetailBlock from "../../../components/History/DetailBlock";

type Props = {};

type PopupType = "satisfaction" | "cancelConfirm" | "";

export default function HistoryService(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const [serviceDetail, setServiceDetail] =
    useState<GoodsServiceRequestDetail>();
  const [popup, setPopup] = useState<PopupType>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingCancel, setIsLoadingCancel] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isLoadingCancel,
  });
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [user] = useUserContext();

  const location = useLocation();

  const loadRentDetail = async () => {
    try {
      if (params.id) {
        const res = await getGoodsServiceDetail(params.id as string);
        if (res.status === HttpStatusCode.Ok) {
          setServiceDetail(res.data);
        }
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("예약 정보를 불러오는데 실패했습니다.");
      }
    }
  };

  const handleCancel = async () => {
    setIsLoadingCancel(true);
    try {
      await cancelGoodsServiceRequest(serviceDetail?.goodsServiceId as string);
      setPopup("");
      open("예약 취소에 성공했습니다.", {
        onClick: async () => {
          await loadRentDetail();
          setIsLoadingCancel(false);
        },
      });
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("예약 취소에 실패했습니다.");
      }
    } finally {
      setIsLoadingCancel(false);
    }
  };

  const getStatusBottomButton = () => {
    switch (serviceDetail?.status) {
      case "REQUESTED":
        return (
          <div className="bg-white flex flex-col">
            <div className="flex">
              <CommonButton
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={() => setPopup("cancelConfirm")}
              >
                예약 취소
              </CommonButton>
            </div>
          </div>
        );
      case "COMPLETED":
        return (
          !serviceDetail?.satisfactionInfo && (
            <div className="bg-white">
              <CommonButton
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={() => {
                  setPopup("satisfaction");
                }}
              >
                만족도 평가
              </CommonButton>
            </div>
          )
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    setIsLoading(true);
    loadRentDetail();
    setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, user.isLoading]);

  useEffect(() => {
    if (user.isLoading) return;
    if (
      serviceDetail?.createdBy &&
      user.memberId !== serviceDetail?.createdBy
    ) {
      navigate("/history", {
        state: {
          error: "다른 사용자의 신청 서비스 정보는 조회할 수 없습니다.",
        },
      });
    }
  }, [user.isLoading, serviceDetail]);

  return (
    <div
      className={clsx([
        {
          "pb-32": ["WAITING", "IN_USE"].includes(serviceDetail?.status ?? ""),
        },
      ])}
    >
      {loadingComponent}
      <StickyHeader
        title="히스토리 상세"
        onBackClick={() =>
          navigate(location?.state?.parentUrl ?? "/history", {
            state: { ...location?.state?.parentState },
          })
        }
        className="px-5"
      />
      <div className="flex flex-col gap-2.5 p-5">
        <div className="text-3xl ">사무용품 신청</div>
        <div className="text-base font-normal text-brand-neutral-40">신청</div>
      </div>
      <div className="flex flex-col gap-2.5 pt-0 px-5 pb-[30px]">
        <div
          className="p-5 flex flex-col gap-2.5 rounded-[10px]"
          style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.10)" }}
        >
          <div className=" bg-brand-floating-primary-button bg-clip-text text-base text-transparent font-semibold">
            {ServiceStatus[serviceDetail?.status ?? "CANCELED"]}
          </div>
          <ProgressBar
            currentStep={
              getServiceRequestStep[serviceDetail?.status ?? "REQUESTED"]
            }
            wrapperClassName="!p-0"
            className="h-2.5 !bg-brand-floating-primary-button"
            rounded
            nonSticky
            totalStep={2}
          />

          {serviceDetail?.status === "COMPLETED" && (
            <>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              {serviceDetail.satisfactionInfo ? (
                <HistorySatisfaction
                  satisfaction={{
                    id: serviceDetail.satisfactionInfo.etcSatisfactionId,
                    score: serviceDetail.satisfactionInfo.satisfaction,
                    comment: serviceDetail.satisfactionInfo.comment,
                    createdAt: serviceDetail.satisfactionInfo.createdAt,
                    createdBy: serviceDetail.satisfactionInfo.createdBy,
                    createdByName: serviceDetail.satisfactionInfo.createdByName,
                    createdByThumbnail:
                      serviceDetail.satisfactionInfo.memberProfileImg?.items.find(
                        (item) => item.scale === "medium"
                      )?.url ?? null,
                    reply:
                      serviceDetail.satisfactionInfo.reply?.etcSatisfactionId &&
                      serviceDetail.satisfactionInfo.reply?.etcSatisfactionId >
                        0
                        ? ({
                            id: serviceDetail.satisfactionInfo.reply
                              ?.etcSatisfactionId,
                            comment:
                              serviceDetail.satisfactionInfo.reply?.comment,
                            createdAt:
                              serviceDetail.satisfactionInfo.reply?.createdAt,
                            createdBy:
                              serviceDetail.satisfactionInfo.reply?.createdBy,
                            createdByName:
                              INCOMMODITY_SATISFACTION_MANAGER.NAME,
                            createdByThumbnail:
                              serviceDetail.satisfactionInfo.reply?.memberProfileImg?.items.find(
                                (item) => item.scale === "medium"
                              )?.url,
                          } as SatisfactionReplyItem)
                        : undefined,
                  }}
                  type="goodsRequest"
                  serviceId={serviceDetail.goodsServiceId}
                  setIsLoading={setIsLoading}
                  open={open}
                  postSatisfaction={async (
                    satisfactionId: number,
                    satisfaction: SatisfactionRequest
                  ) => {
                    await patchEtcSatisfaction(satisfactionId, satisfaction);
                    await loadRentDetail();
                  }}
                />
              ) : (
                <CommonButton
                  variant="secondary"
                  className="px-4 py-[11px] text-sm"
                  onClick={() => {
                    setPopup("satisfaction");
                  }}
                >
                  만족도 평가하기
                </CommonButton>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-10 px-5">
        <HistoryDetailBlock title="서비스 이용 정보">
          <HistoryDetailRow title="분류">
            <div className="font-bold">신청</div>
            <div className="bg-brand-category_tag rounded py-1 pl-1.5 pr-2 flex items-center gap-1.5 text-sm w-fit">
              <div className="w-1 h-1 bg-white rounded-full"></div>
              사무용품 신청
            </div>
            <div className="font-bold">
              {serviceDetail?.requestType === "PICK_UP"
                ? "방문수령"
                : "OA실 리필"}
            </div>
          </HistoryDetailRow>
          <HistoryDetailRow title="접수 시간">
            {serviceDetail?.createdAt &&
              getYearToMinuteFromISODateString(serviceDetail?.createdAt)}
          </HistoryDetailRow>
          <HistoryDetailRow title="접수 번호">
            {serviceDetail?.goodsServiceId}
          </HistoryDetailRow>
          <HistoryDetailRow title="접수 위치">
            {serviceDetail?.buildingName}{" "}
            {serviceDetail?.requestBuildingFloorName} /{" "}
            {serviceDetail?.companyName} {serviceDetail?.createdByName}
          </HistoryDetailRow>
          <HistoryDetailRow title="상세 내용">
            {[
              ...(serviceDetail?.goodsServiceItemList.map(
                (item) => `${item.goodsName} ${item.requestCount}개`
              ) ?? []),
              ...(serviceDetail?.extraRequest
                ? [`(${serviceDetail?.extraRequest})`]
                : []),
            ].join("\n")}
          </HistoryDetailRow>
          {/* TODO: 전달 사항, 구분, 용도, 물품, 받으실 분 */}
          <HistoryDetailRow title="사용 부서">
            {serviceDetail?.usedDepartment}
          </HistoryDetailRow>
          {/* TODO: 게시물 내용 */}
        </HistoryDetailBlock>

        {serviceDetail?.status === "COMPLETED" && (
          <HistoryDetailBlock title="처리/결과">
            <HistoryDetailRow title="처리 완료 시간">
              {getYearToMinuteFromISODateString(
                serviceDetail?.completedAt ?? ""
              )}
            </HistoryDetailRow>
            <HistoryDetailRow title="비고">
              {serviceDetail?.adminNotes}
            </HistoryDetailRow>
          </HistoryDetailBlock>
        )}

        {serviceDetail?.status === "CANCELED" && (
          <HistoryDetailBlock title="취소 정보">
            <HistoryDetailRow title="취소 시간">
              {getYearToMinuteFromISODateString(serviceDetail.canceledAt ?? "")}
            </HistoryDetailRow>
          </HistoryDetailBlock>
        )}

        {getStatusBottomButton() && (
          <BottomGroup className="h-fit">{getStatusBottomButton()}</BottomGroup>
        )}
      </div>

      <CommonPopup isOpen={popup === "cancelConfirm"} padding={0}>
        <div className=" whitespace-pre-line p-5 text-center">
          예약을 취소하시겠습니까?
        </div>
        <div className="flex">
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            variant="secondary"
            onClick={() => setPopup("")}
            disabled={isLoadingCancel}
          >
            취소
          </CommonButton>
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            onClick={handleCancel}
            disabled={isLoadingCancel}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>
      <UserSatisfactionPopup
        popup={popup}
        setPopup={(popup) => setPopup(popup as PopupType)}
        setIsLoading={(isLoading) => setIsLoading(isLoading)}
        open={open}
        postSatisfaction={async (satisfaction: SatisfactionRequest) => {
          await postEtcSatisfaction(params.id as string, {
            ...satisfaction,
            serviceName: "goodsRequest",
          });
          await loadRentDetail();
        }}
      />

      {simplePopupComponent}
    </div>
  );
}

export const ManagerReservationDetailCommentIcon = () => (
  <svg
    width="60"
    height="61"
    viewBox="0 0 60 61"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M45.4093 46.2478C40.7911 50.8666 34.7121 53.1031 28.6712 52.9725C20.4675 52.7952 6.0791 52.924 6.0791 52.924C6.0791 52.924 6.20527 38.367 6.19907 30.0207C6.19471 24.1391 8.43411 18.2587 12.9294 13.7644C21.8902 4.80029 36.4486 4.80029 45.4093 13.762C54.3862 22.74 54.3701 37.2861 45.4093 46.2478Z"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M19.4767 31.0054H19.2187"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M29.2355 31.0054H28.9775"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M38.9943 31.0054H38.7363"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M42.6981 23.9039C48.6086 23.9039 53.4 19.1125 53.4 13.202C53.4 7.29143 48.6086 2.5 42.6981 2.5C36.7875 2.5 31.9961 7.29143 31.9961 13.202C31.9961 19.1125 36.7875 23.9039 42.6981 23.9039Z"
        fill="black"
        stroke="white"
        strokeWidth="4"
      />
      <path
        d="M45.9508 9.94922L39.4453 16.4547"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M39.4453 9.94922L45.9508 16.4547"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ManagerReservationDetailRemoveIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5" width="24" height="24" rx="12" fill="#E7E7E8" />
    <g opacity="0.3">
      <path
        d="M16.5787 7.64941L7.37793 16.8502"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M7.37793 7.64941L16.5787 16.8502"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
